import React, { useMemo, useState } from "react";
import { FiArrowDownRight, FiArrowUpRight, FiExternalLink, FiMinus, FiSearch, FiX } from "react-icons/fi";
import type { PageTraffic, TrafficPoint } from "../../hooks/useAnalytics";
import {
	aggregateSections,
	matchesQuery,
	pageTitleOf,
	sectionColorOf,
	sectionKeyOf,
	sectionLabelOf,
	trendLabel,
	trendOf,
	trendText,
	type SectionAggregate,
	type Trend,
} from "./pagesInsightsModel";

/**
 * "Pages les plus parcourues" : the ranking of the public pages over the window, with, per page, a
 * sparkline of its daily page views, its share of the traffic, the sessions that ENTERED on it, and
 * its trend against the previous window. Two readings of the same data: by page, or grouped by
 * section (first path segment); a section row filters the pages view; a search box narrows both.
 * Colour policy: page views are ONE magnitude (indigo bars); the section colour only ever tags identity.
 */

const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });
const full = new Intl.NumberFormat("fr-FR");
const pct = new Intl.NumberFormat("fr-FR", { style: "percent", maximumFractionDigits: 1 });
const dayFmt = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });

const VIEWS_COLOR = "#6366F1";
const PAGE_STEP = 10;

const parseLocalDay = (iso: string): Date => {
	const [y, m, d] = (iso || "").split("-").map(Number);
	return new Date(y || 1970, (m || 1) - 1, d || 1);
};

/** Small inline daily-page-views curve (area + line), the same shape for every row so they compare. */
export const Sparkline: React.FC<{ series: TrafficPoint[]; color?: string; label?: string }> = ({ series, color = VIEWS_COLOR, label }) => {
	const W = 80;
	const H = 26;
	if (!series || series.length < 2) {
		return <span className="inline-block h-[26px] w-20 text-[10px] leading-[26px] text-slate-300">{series?.length === 1 ? "1 jour" : "-"}</span>;
	}
	const max = Math.max(1, ...series.map((p) => p.pageViews));
	const x = (i: number) => (i / (series.length - 1)) * (W - 4) + 2;
	const y = (v: number) => H - 3 - (v / max) * (H - 6);
	const pts = series.map((p, i) => `${x(i).toFixed(1)},${y(p.pageViews).toFixed(1)}`);
	const line = `M ${pts.join(" L ")}`;
	const area = `${line} L ${x(series.length - 1).toFixed(1)},${H - 1} L ${x(0).toFixed(1)},${H - 1} Z`;
	const last = series[series.length - 1];
	const title = label ?? `${series.length} jours · dernier jour ${dayFmt.format(parseLocalDay(last.date))} : ${full.format(last.pageViews)} pages vues`;
	return (
		<svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="shrink-0" role="img" aria-label={title}>
			<title>{title}</title>
			<path d={area} fill={color} opacity={0.12} />
			<path d={line} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
			<circle cx={x(series.length - 1)} cy={y(last.pageViews)} r={2.2} fill={color} />
		</svg>
	);
};

/** Trend chip: up / down / flat / new, never colour-alone (arrow icon + text). */
export const TrendChip: React.FC<{ trend: Trend; className?: string }> = ({ trend, className }) => {
	const base = "inline-flex items-center gap-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums";
	if (trend.kind === "new") {
		return (
			<span className={`${base} bg-indigo-50 text-indigo-600 ${className ?? ""}`} title={trendLabel(trend)}>
				Nouveau
			</span>
		);
	}
	if (trend.kind === "flat") {
		return (
			<span className={`${base} bg-slate-100 text-slate-500 ${className ?? ""}`} title={trendLabel(trend)}>
				<FiMinus className="h-3 w-3" aria-hidden /> {trendText(trend)}
			</span>
		);
	}
	const up = trend.kind === "up";
	return (
		<span className={`${base} ${up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"} ${className ?? ""}`} title={trendLabel(trend)}>
			{up ? <FiArrowUpRight className="h-3 w-3" aria-hidden /> : <FiArrowDownRight className="h-3 w-3" aria-hidden />}
			{trendText(trend)}
		</span>
	);
};

const SectionChip: React.FC<{ path: string; onClick?: () => void; active?: boolean }> = ({ path, onClick, active }) => {
	const color = sectionColorOf(path);
	const label = sectionLabelOf(path);
	const cls = `inline-flex max-w-[160px] items-center gap-1 truncate rounded-full border px-1.5 py-px text-[10px] font-semibold ${
		active ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-600"
	}`;
	const style = active ? { background: color } : undefined;
	const dot = <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: active ? "#fff" : color }} aria-hidden />;
	return onClick ? (
		<button type="button" onClick={onClick} className={`${cls} hover:border-slate-300`} style={style} title={`Filtrer sur la rubrique ${label}`}>
			{dot}
			<span className="truncate">{label}</span>
		</button>
	) : (
		<span className={cls} style={style}>
			{dot}
			<span className="truncate">{label}</span>
		</span>
	);
};

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => (
	<span
		className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold tabular-nums ${
			rank <= 3 ? "bg-[#0F2849] text-white" : "bg-slate-100 text-slate-500"
		}`}
	>
		{rank}
	</span>
);

const ShareBar: React.FC<{ share: number; color?: string }> = ({ share, color = VIEWS_COLOR }) => (
	<div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden>
		<div className="h-full rounded-full" style={{ width: `${Math.max(1.5, Math.round(share * 1000) / 10)}%`, background: color }} />
	</div>
);

type Props = {
	pages: PageTraffic[];
	/** Uncapped number of distinct pages seen (the ranking itself is capped server-side). */
	pageCount?: number;
	/** Public host used to open a page ("https://{host}{path}"). */
	host?: string | null;
	days: number;
	loading?: boolean;
};

export const PagesInsights: React.FC<Props> = ({ pages, pageCount, host, days, loading }) => {
	const distinct = pageCount ?? pages.length;
	const [mode, setMode] = useState<"pages" | "sections">("pages");
	const [query, setQuery] = useState("");
	const [sectionKey, setSectionKey] = useState<string | null>(null);
	const [shown, setShown] = useState(PAGE_STEP);

	const total = useMemo(() => pages.reduce((s, p) => s + p.pageViews, 0), [pages]);
	const filtered = useMemo(
		() => pages.filter((p) => (sectionKey === null || sectionKeyOf(p.path) === sectionKey) && matchesQuery(p, query)),
		[pages, query, sectionKey],
	);
	const sections = useMemo(() => aggregateSections(pages.filter((p) => matchesQuery(p, query))), [pages, query]);
	const visible = filtered.slice(0, shown);
	const filteredViews = filtered.reduce((s, p) => s + p.pageViews, 0);

	const openUrl = (path: string) => (host ? `https://${host}${path}` : null);
	const pickSection = (key: string) => {
		setSectionKey(key);
		setMode("pages");
		setShown(PAGE_STEP);
	};
	const sectionLabel = sectionKey !== null ? sectionLabelOf("/" + sectionKey) : null;

	return (
		<div className="min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm">
			{/* Header : title + subtitle, then the two controls (view switch, search) on one row. */}
			<div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4">
				<div>
					<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Pages les plus parcourues</p>
					<p className="mt-0.5 text-xs text-slate-500">
						{loading
							? "Chargement…"
							: `${full.format(distinct)} page${distinct > 1 ? "s" : ""} consultée${distinct > 1 ? "s" : ""} sur ${days} jour${days > 1 ? "s" : ""} · ${full.format(total)} pages vues au total${
									pages.length < distinct ? ` · top ${full.format(pages.length)} affiché` : ""
								}`}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="tablist" aria-label="Vue">
						{(["pages", "sections"] as const).map((m) => (
							<button
								key={m}
								type="button"
								role="tab"
								aria-selected={mode === m}
								onClick={() => setMode(m)}
								className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${mode === m ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
							>
								{m === "pages" ? "Pages" : "Rubriques"}
							</button>
						))}
					</div>
					<label className="relative block">
						<FiSearch className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden />
						<input
							type="search"
							value={query}
							onChange={(e) => {
								setQuery(e.target.value);
								setShown(PAGE_STEP);
							}}
							placeholder="Rechercher une page…"
							aria-label="Rechercher une page"
							className="w-48 rounded-lg border border-slate-200 bg-white py-1 pl-7 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
						/>
					</label>
				</div>
			</div>

			{/* Active section filter. */}
			{sectionKey !== null && (
				<div className="flex items-center gap-2 px-5 pt-3 text-xs text-slate-500">
					<span>Rubrique :</span>
					<SectionChip path={"/" + sectionKey} active />
					<button
						type="button"
						onClick={() => setSectionKey(null)}
						className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
						aria-label={`Retirer le filtre ${sectionLabel ?? ""}`}
					>
						<FiX className="h-3 w-3" aria-hidden /> Retirer
					</button>
					<span className="ml-auto tabular-nums">
						{full.format(filtered.length)} page{filtered.length > 1 ? "s" : ""} · {full.format(filteredViews)} pages vues
					</span>
				</div>
			)}

			<div className="px-2 pb-3 pt-2 sm:px-3">
				{loading ? (
					<ul className="space-y-1.5 px-2 py-2" aria-hidden>
						{Array.from({ length: 6 }).map((_, i) => (
							<li key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
						))}
					</ul>
				) : pages.length === 0 ? (
					<p className="px-3 py-10 text-center text-xs text-slate-400">Aucune page mesurée sur cette période.</p>
				) : mode === "sections" ? (
					<SectionsTable sections={sections} onPick={pickSection} />
				) : filtered.length === 0 ? (
					<p className="px-3 py-10 text-center text-xs text-slate-400">Aucune page ne correspond à « {query.trim()} ».</p>
				) : (
					<>
						<div className="overflow-x-auto">
							<table className="w-full min-w-[540px] border-separate border-spacing-0 text-left">
								<thead>
									<tr className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
										<th className="px-2 pb-1.5 pt-1 font-semibold" scope="col">#</th>
										<th className="px-2 pb-1.5 pt-1 font-semibold" scope="col">Page</th>
										<th className="px-2 pb-1.5 pt-1 font-semibold" scope="col">Tendance</th>
										<th className="px-2 pb-1.5 pt-1 text-right font-semibold" scope="col">Pages vues</th>
										<th className="px-2 pb-1.5 pt-1 text-right font-semibold" scope="col">Part</th>
										<th className="px-2 pb-1.5 pt-1 text-right font-semibold" scope="col" title="Visites ayant commencé sur cette page">Entrées</th>
										<th className="px-2 pb-1.5 pt-1 text-right font-semibold" scope="col" title="Par rapport à la période précédente de même durée">Évolution</th>
										<th className="px-2 pb-1.5 pt-1" scope="col"><span className="sr-only">Ouvrir</span></th>
									</tr>
								</thead>
								<tbody>
									{visible.map((p, i) => {
										const share = total > 0 ? p.pageViews / total : 0;
										const url = openUrl(p.path);
										const t = trendOf(p.pageViews, p.previousPageViews);
										return (
											<tr key={p.path} className="group text-sm text-slate-700 hover:bg-slate-50">
												<td className="rounded-l-lg px-2 py-1.5 align-middle">
													<RankBadge rank={i + 1} />
												</td>
												<td className="max-w-[260px] px-2 py-1.5 align-middle">
													<div className="flex items-center gap-2">
														<span className="truncate font-semibold text-slate-800" title={p.path}>
															{pageTitleOf(p.path)}
														</span>
														<SectionChip path={p.path} onClick={() => pickSection(sectionKeyOf(p.path))} />
													</div>
													<div className="truncate font-mono text-[11px] text-slate-400" title={p.path}>
														{p.path}
													</div>
												</td>
												<td className="px-2 py-1.5 align-middle">
													<Sparkline series={p.series} />
												</td>
												<td className="w-20 px-2 py-1.5 text-right align-middle">
													<span className="font-semibold tabular-nums text-slate-800" title={full.format(p.pageViews)}>
														{compact.format(p.pageViews)}
													</span>
													<ShareBar share={share} />
												</td>
												<td className="px-2 py-1.5 text-right align-middle text-xs tabular-nums text-slate-500">{pct.format(share)}</td>
												<td className="px-2 py-1.5 text-right align-middle text-xs tabular-nums text-slate-500" title={full.format(p.visits)}>
													{compact.format(p.visits)}
												</td>
												<td className="px-2 py-1.5 text-right align-middle">
													<TrendChip trend={t} />
												</td>
												<td className="rounded-r-lg px-2 py-1.5 text-right align-middle">
													{url ? (
														<a
															href={url}
															target="_blank"
															rel="noreferrer"
															className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-300 transition hover:bg-white hover:text-indigo-600 group-hover:text-slate-400"
															aria-label={`Ouvrir ${p.path} sur le site`}
															title={url}
														>
															<FiExternalLink className="h-3.5 w-3.5" />
														</a>
													) : null}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						{filtered.length > PAGE_STEP && (
							<div className="flex items-center justify-between px-3 pt-2 text-xs text-slate-500">
								<span className="tabular-nums">
									{full.format(Math.min(shown, filtered.length))} / {full.format(filtered.length)} pages
								</span>
								<div className="flex items-center gap-2">
									{shown < filtered.length && (
										<button type="button" onClick={() => setShown((n) => n + PAGE_STEP)} className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-600 hover:bg-slate-50">
											Afficher {Math.min(PAGE_STEP, filtered.length - shown)} de plus
										</button>
									)}
									{shown > PAGE_STEP && (
										<button type="button" onClick={() => setShown(PAGE_STEP)} className="rounded-md px-2.5 py-1 font-semibold text-slate-500 hover:bg-slate-100">
											Réduire
										</button>
									)}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

const SectionsTable: React.FC<{ sections: SectionAggregate[]; onPick: (key: string) => void }> = ({ sections, onPick }) => {
	const max = sections[0]?.pageViews ?? 0;
	if (sections.length === 0) return <p className="px-3 py-10 text-center text-xs text-slate-400">Aucune rubrique pour cette recherche.</p>;
	return (
		<ul className="divide-y divide-slate-50">
			{sections.map((s, i) => {
				const t = trendOf(s.pageViews, s.previousPageViews);
				return (
					<li key={s.key || "home"}>
						<button
							type="button"
							onClick={() => onPick(s.key)}
							className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-50"
							title={`Voir les pages de la rubrique ${s.label}`}
						>
							<RankBadge rank={i + 1} />
							<div className="min-w-0 flex-1">
								<div className="flex items-center gap-2">
									<span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} aria-hidden />
									<span className="truncate text-sm font-semibold text-slate-800">{s.label}</span>
									<span className="shrink-0 text-[11px] text-slate-400">
										{s.pageCount} page{s.pageCount > 1 ? "s" : ""}
									</span>
									<span className="ml-auto text-xs tabular-nums text-slate-500" title="Visites ayant commencé dans cette rubrique">
										{compact.format(s.visits)} entrées
									</span>
									<span className="w-24 text-right text-sm font-semibold tabular-nums text-slate-800" title={full.format(s.pageViews)}>
										{compact.format(s.pageViews)}
									</span>
									<span className="w-14 text-right text-xs tabular-nums text-slate-500">{pct.format(s.share)}</span>
									<TrendChip trend={t} className="w-16 justify-center" />
								</div>
								<div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden>
									<div className="h-full rounded-full" style={{ width: `${max > 0 ? Math.max(1.5, (s.pageViews / max) * 100) : 0}%`, background: s.color }} />
								</div>
							</div>
						</button>
					</li>
				);
			})}
		</ul>
	);
};

export default PagesInsights;
